import { AddIcon } from "@minket/brand/icons/add";
import { DomainsIcon } from "@minket/brand/icons/domains";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { day } from "@/components/domains/format";
import { Status } from "@/components/domains/status";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { ProjectScope } from "@/components/page/scope";
import { Seams } from "@/components/page/seams";
import { QueryView } from "@/components/page/states";
import { endpoints, type ResponseOf } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/domains/")({
  component: Domains,
});

const AddDomainDialog = lazy(() =>
  import("@/components/domains/add-domain.ondemand").then((m) => ({ default: m.AddDomainDialog })),
);
/** The micro seals load with the seal geometry, on the domain routes only. */
const MicroSeal = lazy(() =>
  import("@/components/domains/micro-seal").then((m) => ({ default: m.MicroSeal })),
);

type Listed = ResponseOf<typeof endpoints.domains>["data"][number];

const columns = ["Domain", "Status", "Authentication", "Regions", "Added"] as const;
const widths = [undefined, "11rem", "10rem", "13rem", "7.5rem"] as const;
const will =
  "Each domain will be listed here with its seal, its status in each region and its stream.";

/** One sentence that says how the domains stand, from the list. */
function standing(domains: readonly Listed[]): string {
  if (domains.length === 0) return "Add the domain you send from; Minket gives you its records.";
  const waiting = domains.filter((d) => d.status !== "verified").length;
  const failing = domains.filter((d) => d.authentication === "failing").length;
  const count = domains.length === 1 ? "One domain" : `${domains.length} domains`;
  if (failing > 0) {
    return `${count}; ${failing === 1 ? "one is" : `${failing} are`} held back until authentication passes.`;
  }
  if (waiting > 0) {
    return `${count}; ${waiting === 1 ? "one waits" : `${waiting} wait`} for DNS.`;
  }
  return `${count}, every one verified.`;
}

function Domains() {
  const { project } = useWorkspace();
  const [adding, setAdding] = useState(false);
  const canManage = useCan("domains.manage");
  const query = useQuery({
    ...apiQuery(endpoints.domains, { projectId: project?.id ?? "" }, { limit: 100 }),
    enabled: !!project,
  });
  const preview = (busy: boolean) => (
    <GhostTable columns={columns} widths={widths} label="domains" busy={busy} />
  );
  return (
    <Page
      title="Domains"
      description={query.data ? standing(query.data.data) : undefined}
      action={
        !project ? (
          <UnavailableAction label="Add domain" icon={AddIcon} reason="Pick a project first" />
        ) : !canManage ? (
          <UnavailableAction
            label="Add domain"
            icon={AddIcon}
            reason="Viewers cannot add domains"
          />
        ) : (
          <Button onClick={() => setAdding(true)}>
            <AddIcon className="size-5" />
            Add domain
          </Button>
        )
      }
    >
      <ProjectScope will={will} icon={<DomainsIcon />} preview={preview}>
        {() => (
          <QueryView query={query} noun="domains" preview={preview}>
            {(list) =>
              list.data.length === 0 ? (
                <EmptyState
                  data-state="empty"
                  icon={<DomainsIcon />}
                  title="No domains yet"
                  description="Add the domain you send from. Its DKIM, MAIL FROM, DMARC and tracking records come next, written on a Cloudflare zone or shown to copy."
                  preview={preview(false)}
                />
              ) : (
                <DomainLedger domains={list.data} />
              )
            }
          </QueryView>
        )}
      </ProjectScope>
      {adding && project ? (
        <Suspense fallback={null}>
          <AddDomainDialog projectId={project.id} onClose={() => setAdding(false)} />
        </Suspense>
      ) : null}
    </Page>
  );
}

function DomainLedger({ domains }: { domains: readonly Listed[] }) {
  return (
    <Table aria-label="Domains of this project" className="min-w-[44rem]">
      <colgroup>
        {widths.map((width, index) => (
          <col key={columns[index]} style={{ width }} />
        ))}
      </colgroup>
      <TableHeader>
        <TableRow>
          {columns.map((column, index) => (
            <TableHead key={column} className={index === columns.length - 1 ? "text-right" : ""}>
              {column}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {domains.map((domain) => (
          <TableRow key={domain.id} className="[&>td]:py-3 [&>td]:align-middle">
            <TableCell>
              <span className="flex min-w-0 items-center gap-3.5">
                <Suspense fallback={<span aria-hidden className="size-9 shrink-0" />}>
                  <MicroSeal domain={domain} size={36} />
                </Suspense>
                <span className="grid min-w-0">
                  <Link
                    to="/domains/$id"
                    params={{ id: domain.id }}
                    className="type-data w-fit rounded-sm text-[14px] font-semibold text-foreground underline decoration-border decoration-1 underline-offset-4 hover:decoration-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  >
                    <Seams>{domain.name}</Seams>
                  </Link>
                  <span className="text-[13px] text-muted-foreground">
                    {domain.stream === "marketing" ? "Marketing" : "Transactional"}
                    {domain.receiving === "enabled" ? ", receives mail" : ""}
                  </span>
                </span>
              </span>
            </TableCell>
            <TableCell>
              <Status value={domain.status} />
            </TableCell>
            <TableCell>
              <Status value={domain.authentication} />
            </TableCell>
            <TableCell>
              <span className="grid gap-1">
                {[domain.primary, domain.secondary].map((region) =>
                  region ? (
                    <span
                      key={region.region}
                      className="flex items-center gap-2.5 whitespace-nowrap"
                    >
                      <span className="type-data text-[13px]">{region.region}</span>
                      <Status value={region.status} className="text-[13px]" />
                    </span>
                  ) : null,
                )}
              </span>
            </TableCell>
            <TableCell className="type-data text-right whitespace-nowrap text-muted-foreground">
              <time dateTime={domain.created_at}>{day(domain.created_at)}</time>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
